/**
 * Couche d'abstraction IPC pour le web
 *
 * Ce fichier simule l'API IPC d'Electron dans un environnement web en utilisant
 * les DOM Events (CustomEvent + addEventListener). Cela permet d'utiliser le même
 * code de communication inter-processus dans les versions web et desktop de l'app.
 *
 * Fonctionnement :
 * - Les channels IPC sont mappés sur des CustomEvent DOM
 * - Les handlers sont stockés et gérés via addEventListener/removeEventListener
 * - Les appels invoke() utilisent un système de promesses avec des channels de retour
 * - Le contextBridge expose les APIs sur window.electron/window.process
 */

import { forEach, uniqueId } from "lodash"

import { Channel } from "types/Channel"

import {
  ContextBridge,
  EmiterFunction,
  EventHandler,
  HandlerEvent,
  IpcRendererEvent,
  IpcRenderer,
  Shell,
  IpcMain,
} from "./WebElectronTypes"

// Registre global des event handlers pour chaque channel IPC
// Permet de tracker et nettoyer les listeners DOM
const events: Record<Channel, EventHandler[]> = {}

/**
 * Crée un objet IpcRendererEvent compatible avec l'API Electron
 * @param ipcRenderer - L'instance IpcRenderer qui sera le sender
 * @returns Un événement IPC formaté
 */
function getEvent(ipcRenderer: IpcRenderer): IpcRendererEvent {
  return {
    ports: [], // Ports de MessageChannel (non utilisés dans cette implémentation)
    sender: ipcRenderer, // Référence vers l'expéditeur
  }
}

/**
 * Nettoie tous les event listeners DOM enregistrés
 * Utilisé pour éviter les fuites mémoire lors du reset de l'app
 */
function clear() {
  forEach(events, (handlers, channel) => {
    handlers.map((handler) => window.removeEventListener(channel, handler as (e: Event) => void))
    delete events[channel]
  })
}

/**
 * Implémentation web de l'IpcRenderer d'Electron
 * Utilise les DOM Events pour simuler la communication IPC
 */
const ipcRenderer: IpcRenderer = {
  /**
   * Écoute en permanence les messages sur un channel
   * @param channel - Le channel IPC à écouter
   * @param emiter - La fonction callback à exécuter
   */
  on(channel: Channel, emiter: EmiterFunction) {
    function handler(event: HandlerEvent) {
      const ev = getEvent(ipcRenderer)
      // Exécute le callback avec les arguments reçus
      emiter(ev, ...(event?.detail?.args || []))
      // Permet la communication synchrone via returnValue
      if (event.detail) event.detail.result = ev.returnValue
    }
    // Enregistre le handler dans notre registre
    if (!events[channel]) events[channel] = []
    events[channel].push(handler)
    // Ajoute l'event listener DOM
    window.addEventListener(channel, handler as (e: Event) => void)
    return ipcRenderer
  },
  /**
   * Écoute un seul message sur un channel puis se désabonne automatiquement
   * @param channel - Le channel IPC à écouter
   * @param emiter - La fonction callback à exécuter une seule fois
   */
  once(channel: Channel, emiter: EmiterFunction) {
    const removeSelf = () => this.removeListener(channel, emiter)
    function handler(event: HandlerEvent) {
      // Exécute le callback une seule fois
      const emiterResult = emiter(getEvent(ipcRenderer), ...(event?.detail?.args || []))
      if (event.detail) event.detail.result = emiterResult
      // Auto-nettoyage après exécution
      removeSelf()
    }
    if (!events[channel]) events[channel] = []
    window.addEventListener(channel, handler as (e: Event) => void)
  },
  /**
   * Invoque une méthode IPC de manière asynchrone (équivalent à ipcRenderer.invoke)
   * Utilise un système de channels de retour avec identifiants uniques
   * @param channel - Le channel IPC à invoquer
   * @param args - Les arguments à passer
   * @returns Une Promise avec la réponse
   */
  invoke<R = any>(channel: Channel, ...args: any[]): Promise<R> {
    return new Promise((resolve, reject) => {
      // Génère un ID unique pour cette invocation
      const id = uniqueId("webIpcRenderer")
      // Écoute les channels de retour (success/error) avec l'ID unique
      this.once(`${channel}__IPC_INVOKE_SUCCESS_${id}`, (e, arg) => resolve(arg))
      this.once(`${channel}__IPC_INVOKE_ERROR_${id}`, (e, arg) => reject(arg))
      // Envoie l'événement avec l'ID pour permettre la réponse
      const event = new CustomEvent(channel, { detail: { args, id } })
      window.dispatchEvent(event)
    })
  },
  rawListeners(channel: Channel): Function[] {
    return (events[channel] || []) as Function[]
  },
  removeListener(channel: Channel, emiter: Function) {
    events[channel]?.forEach((handler, index) => {
      if (handler === emiter) {
        window.removeEventListener(channel, handler as (e: Event) => void)
        events[channel].splice(index, 1)
      }
    })
    return ipcRenderer
  },
  removeAllListeners(channel?: Channel): IpcRenderer {
    if (!channel) {
      clear()
      return ipcRenderer
    }
    const handlers = events[channel]
    handlers?.map((handler) => window.removeEventListener(channel, handler as (e: Event) => void))
    delete events[channel]
    return ipcRenderer
  },
  /**
   * Envoie un message asynchrone sur un channel (fire-and-forget)
   * @param channel - Le channel IPC de destination
   * @param args - Les arguments à envoyer
   */
  send(channel: Channel, ...args: any[]) {
    const evt = new CustomEvent(channel, { detail: { args } })
    window.dispatchEvent(evt)
  },

  /**
   * Envoie un message synchrone et attend la réponse immédiatement
   * @param channel - Le channel IPC de destination
   * @param args - Les arguments à envoyer
   * @returns La réponse synchrone du handler
   */
  sendSync<R = void>(channel: Channel, ...args: any[]): R {
    const event = new CustomEvent<{ args: any[]; result?: R }>(channel, {
      detail: { args },
    })
    window.dispatchEvent(event)
    // Récupère le résultat directement depuis l'événement modifié par le handler
    return event.detail?.result as R
  },
}

/**
 * Implémentation web de l'IpcMain d'Electron
 * Hérite de toutes les méthodes d'IpcRenderer + ajoute handle()
 */
const ipcMain: IpcMain = {
  ...ipcRenderer,

  /**
   * Enregistre un handler pour répondre aux appels invoke()
   * Gère automatiquement les réponses success/error via les channels de retour
   * @param channel - Le channel IPC à gérer
   * @param emiter - La fonction qui traite les requêtes invoke
   */
  handle<R = any>(channel: Channel, emiter: EmiterFunction<R>) {
    const sender = this.send
    function handler(event: HandlerEvent) {
      const { args = [], id } = event.detail || {}
      // Exécute le handler et gère les Promises
      const emiterResult = emiter(getEvent(ipcMain), ...args)
      Promise.resolve(emiterResult)
        .then((res) => {
          // Renvoie la réponse sur le channel de success
          sender(`${channel}__IPC_INVOKE_SUCCESS_${id}`, res)
        })
        .catch((error) => {
          // Renvoie l'erreur sur le channel d'error
          sender(`${channel}__IPC_INVOKE_ERROR_${id}`, error)
        })
    }
    if (!events[channel]) events[channel] = []
    events[channel].push(handler)
    window.addEventListener(channel, handler as (e: Event) => void)
  },
}

/**
 * Implémentation web du shell d'Electron
 * Permet d'ouvrir des URLs externes dans le navigateur
 */
const shell: Shell = {
  /**
   * Ouvre une URL dans un nouvel onglet/fenêtre du navigateur
   * @param url - L'URL à ouvrir
   * @param option - Options d'ouverture (target, features)
   */
  openExternal: (url: string, option: any): Promise<void> => {
    const { target, features } = option
    // Utilise window.open() pour simuler l'ouverture externe
    window.open(url, target || "_blank", features)?.focus()
    return Promise.resolve()
  },
}

/**
 * Implémentation web du contextBridge d'Electron
 * Expose les APIs sur l'objet window global
 */
const contextBridge: ContextBridge = {
  /**
   * Expose un objet dans le contexte global de la fenêtre
   * @param name - Le nom de la propriété sur window (electron/process)
   * @param obj - L'objet à exposer
   */
  exposeInMainWorld: (name: "electron" | "process" | "windowName", obj: any) => {
    window[name] = obj
  },
}

export { contextBridge, ipcRenderer, ipcMain, shell }
